import * as Sentry from "@sentry/react";

import {
  type CommonSentryConfigInput,
  getCommonSentryConfig,
} from "../lib/sentry-common";

// Track if Sentry has been initialized to avoid double initialization
let sentryInitialized = false;

export function isSentryInitialized() {
  return sentryInitialized;
}

export function initializeSentry({
  dsn,
  environment = "production",
  version = "0.0.0",
  tenant,
}: Partial<CommonSentryConfigInput> & { tenant?: string }) {
  if (!dsn) {
    return;
  }

  if (sentryInitialized) {
    return;
  }
  sentryInitialized = true;

  const config = getCommonSentryConfig({ dsn, environment, version });

  const client = Sentry.init({
    ...config,

    integrations: [
      // this should be replaced with tanstack router integration
      Sentry.browserTracingIntegration(),
      //  session-replay
      Sentry.replayIntegration(),
    ],

    tunnel: "/api/tunnel",

    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    // tracePropagationTargets: [/^\/api/],

    // session-replay
    // Capture Replay for 20% of all sessions, plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.2,
    replaysOnErrorSampleRate: 1.0,
  });

  Sentry.getGlobalScope().setTags({
    tenant: tenant,
  });

  return client;
}

// biome-ignore lint/suspicious/noExplicitAny: nah
export function setSentryRouter(router: any) {
  if (!isSentryInitialized()) {
    return;
  }
  Sentry.addIntegration(Sentry.tanstackRouterBrowserTracingIntegration(router));
}

export function setSentryUser(user: Sentry.User) {
  if (!isSentryInitialized()) {
    return;
  }
  Sentry.setUser(user);
}

export function captureSentryException(
  error: unknown,
  context?: Parameters<typeof Sentry.captureException>[1],
) {
  if (!isSentryInitialized()) {
    return;
  }
  Sentry.captureException(error, context);
}

export function captureSentryMessage(
  message: string,
  context?: Parameters<typeof Sentry.captureMessage>[1],
) {
  if (!isSentryInitialized()) {
    return;
  }
  Sentry.captureMessage(message, context);
}

export { Sentry };
